import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';

import { fetchSettings } from '../../../store/modules/dailyproduction/dailyproductionActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { useHasPermission } from '../../../utils/permissionUtils';

const DailyProductionForm = ({ initialData, onSave, isLoading, onClose }) => {

    const hasCreatePermission = useHasPermission(['create daily productions']);
    const hasUpdatePermission = useHasPermission(['update daily productions']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('dailyproduction', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        id: '',
        fg_produced_qty: '',
        fg_qty: '',
        fg_total: '',
        issue_date: '',
        issue_number: '',
        issued_to: '',
        issued_to_ref: '',
        item_name: '',
        order_number: '',
        qty_to_update: '',

        fg_unit_price: '',
        unit_percentage: '',
        unit_price: '',
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                issue_date: initialData.issue_date || '',
                fg_produced_qty: initialData.fg_produced_qty || '',
                fg_qty: initialData.fg_qty || '',
                fg_total: initialData.fg_total || '',
                issue_number: initialData.issue_number || '',
                issued_to: initialData.issued_to || '',
                issued_to_ref: initialData.issued_to_ref || '',
                item_name: initialData.item_name || '',
                order_number: initialData.order_number || '',
                qty_to_update: initialData.qty_to_update || parseFloat(initialData.fg_qty) - parseFloat(initialData.fg_produced_qty) || 0,

                fg_unit_price: initialData.fg_unit_price || '',
                unit_percentage: initialData.unit_percentage || '',
                unit_price: initialData.unit_price || '',
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Validate qty_to_update against remaining quantity
        if (name === 'qty_to_update') {
            const totalIssued = parseFloat(formData.fg_qty) || 0;
            const alreadyProduced = parseFloat(formData.fg_produced_qty) || 0;
            const newQtyToUpdate = parseFloat(value) || 0;
            const remainingQty = totalIssued - alreadyProduced;

            if (newQtyToUpdate > remainingQty) {
                setErrors(prev => ({
                    ...prev,
                    qty_to_update: `Quantity to update cannot exceed remaining quantity of ${remainingQty.toFixed(3)}`
                }));
            } else if (errors.qty_to_update) {
                setErrors(prev => ({...prev, qty_to_update: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleFgPercentageChange = async (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            let newData = { ...prev };

            return {
                ...newData,
                [name]: value,
                fg_unit_price: ((value * newData.unit_price) / 100).toFixed(3),
            };
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate qty_to_update before submission
        const totalIssued = parseFloat(formData.fg_qty) || 0;
        const alreadyProduced = parseFloat(formData.fg_produced_qty) || 0;
        const qtyToUpdate = parseFloat(formData.qty_to_update) || 0;
        const remainingQty = totalIssued - alreadyProduced;

        if (qtyToUpdate > remainingQty) {
            setErrors(prev => ({
                ...prev,
                qty_to_update: `Quantity to update cannot exceed remaining quantity of ${remainingQty.toFixed(3)}`
            }));
            return;
        }

        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">
                
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Item Name</label>
                            <div className="form-control-plaintext">
                                {formData.item_name}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Issued To</label>
                            <div className="form-control-plaintext">
                                {formData.issued_to_ref}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Total Issued</label>
                            <CommonInput
                                name="fg_qty"
                                value={formData.fg_qty}
                                onChange={handleChange}
                                errors={errors}
                                disabled={true}
                                required={false}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Produced Qty.</label>
                            <CommonInput
                                name="fg_produced_qty"
                                value={formData.fg_produced_qty || 0}
                                onChange={handleChange}
                                errors={errors}
                                disabled={true}
                                required={false}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Qty. to Update</label>
                            <CommonInput
                                name="qty_to_update"
                                value={formData.qty_to_update || 0}
                                onChange={handleChange}
                                errors={errors}
                                required={false}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Unit Price (Labour per unit)</label>
                            <CommonInput
                                name="fg_unit_price"
                                value={formData.fg_unit_price || 0}
                                onChange={handleChange}
                                errors={errors}
                                required={false}
                                disabled={isSubmitting || formData.unit_percentage > 0}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Unit Percentage (Labour per unit)</label>
                            <CommonInput
                                name="unit_percentage"
                                value={formData.unit_percentage || 0}
                                errors={errors}
                                required={false}
                                disabled={isSubmitting}
                                onChange={handleFgPercentageChange}
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3 ">
                            <label className="form-label fw-500">Total</label>
                            <CommonInput
                                name="total"
                                value={((formData.qty_to_update || 0) * (formData.fg_unit_price || 0)).toFixed(3)}
                                errors={errors}
                                required={false}
                                disabled={true}
                            />
                        </div>
                    </div>

                </div>
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default DailyProductionForm; 